import React, { useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./AppShell.css";
import { supabase } from "../../supabaseClient";
import { useEffect, useState } from "react";
import {
  DashboardIcon,
  LicenseIcon,
  VerificationIcon,
  ProfileIcon,
  LookupIcon,
  FindProIcon,
  AdminIcon,
  SignOutIcon,
  LeadsIcon,
  PlansIcon,
} from "../../assets/icons/icons";
import logoColor from "../../assets/clearbadge-logo-color.png";
import useFocusTrap from "../../hooks/useFocusTrap";

const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";
const LOGO_ICON_PUBLIC_PATH = "/logo512.png";

const navItems = [
  { label: "Overview", path: "/dashboard", icon: "dashboard" },
  { label: "Licenses", path: "/licenses", icon: "license" },
  { label: "Leads", path: "/leads", icon: "leads" },
  { label: "Verification", path: "/verification", icon: "verification" },
  { label: "Lookup", path: "/lookup", icon: "lookup" },
  { label: "Find a Pro", path: "/find-pro", icon: "findPro" },
  { label: "Plans", path: "/plans", icon: "plans" },
  { label: "My Info", path: "/my-info", icon: "profile" },
];

const iconMap = {
  dashboard: <DashboardIcon className="icon" />,
  license: <LicenseIcon className="icon" />,
  leads: <LeadsIcon className="icon" />,
  verification: <VerificationIcon className="icon" />,
  lookup: <LookupIcon className="icon" />,
  findPro: <FindProIcon className="icon" />,
  profile: <ProfileIcon className="icon" />,
  plans: <PlansIcon className="icon" />,
  admin: <AdminIcon className="icon" />,
};

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef(null);
  const drawerRef = useRef(null);
  const drawerBrandRef = useRef(null);
  const drawerToggleRef = useRef(null);

  useFocusTrap({
    isOpen: isMobile && drawerOpen,
    containerRef: drawerRef,
    onClose: () => setDrawerOpen(false),
    initialFocusRef: drawerBrandRef,
    returnFocusRef: drawerToggleRef,
    lockScroll: true,
  });

  useEffect(() => {
    document.body.classList.add("app-shell");
    return () => {
      document.body.classList.remove("app-shell");
    };
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setSidebarCollapsed(false);
  }, [isMobile]);

  const handleScrollState = () => {
    const scrollY = contentRef.current ? contentRef.current.scrollTop : window.scrollY;
    setScrolled(scrollY > 4);
  };

  useEffect(() => {
    handleScrollState();
    window.addEventListener("scroll", handleScrollState, { passive: true });
    return () => window.removeEventListener("scroll", handleScrollState);
  }, []);

  useEffect(() => {
    const loadAdmin = async () => {
      const { data } = await supabase.auth.getSession();
      const userId = data?.session?.user?.id;
      if (!userId) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("id", userId)
        .maybeSingle();
      setIsAdmin(!!profile?.is_admin);
    };
    loadAdmin();
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      // Fallback to local scope sign out if global is forbidden in browser
      await supabase.auth.signOut({ scope: "local" }).catch(() => {});
    }
    // Clear any cached session regardless
    await supabase.auth._removeSession();
    navigate("/login");
  };

  const isEdgeToEdgeRoute =
    location.pathname.startsWith("/lookup") ||
    location.pathname.startsWith("/find-pro") ||
    location.pathname.startsWith("/plans");
  const isLookupFindProRoute =
    location.pathname.startsWith("/lookup") || location.pathname.startsWith("/find-pro");
  const isPlansRoute = location.pathname.startsWith("/plans");
  const isDesktopCollapsed = !isMobile && sidebarCollapsed;
  const brandLogoSrc = isDesktopCollapsed ? LOGO_ICON_PUBLIC_PATH : isMobile ? LOGO_WHITE_PUBLIC_PATH : logoColor;
  const handleSidebarToggle = (event) => {
    setSidebarCollapsed((collapsed) => !collapsed);
    event.currentTarget.blur();
  };

  return (
    <div className={`shell ${isDesktopCollapsed ? "sidebar-collapsed" : ""}`}>
      {isMobile && drawerOpen && (
        <button
          type="button"
          className="drawer-overlay"
          onClick={() => setDrawerOpen(false)}
          aria-label="Close navigation menu"
        />
      )}
      <aside
        id="app-shell-navigation"
        ref={drawerRef}
        className={`sidebar ${isMobile ? "mobile-drawer" : ""} ${drawerOpen ? "open" : ""} ${!isMobile ? (sidebarCollapsed ? "desktop-collapsed" : "desktop-expanded") : ""}`}
        {...(isMobile
          ? { role: "dialog", "aria-modal": "true", "aria-label": "Navigation menu" }
          : {})}
      >
        <div className="shell-drawer-header">
          <button
            ref={drawerBrandRef}
            type="button"
            className="brand brand-btn"
            onClick={() => {
              navigate("/dashboard");
              setDrawerOpen(false);
            }}
          >
            <img src={brandLogoSrc} alt="ClearBadge logo" className="brand-logo" />
          </button>
          {!isMobile && (
            <button
              type="button"
              className="icon-btn sidebar-collapse-btn"
              onClick={handleSidebarToggle}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!sidebarCollapsed}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="sidebar-collapse-icon"
                aria-hidden="true"
              >
                <path d={sidebarCollapsed ? "m9 18 6-6-6-6" : "m15 18-6-6 6-6"} />
              </svg>
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              className="icon-btn shell-mobile-close"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close menu"
            >
              <span className="mobile-drawer-close-icon" aria-hidden="true" />
            </button>
          )}
        </div>
        <nav className="nav-group" aria-label="Primary navigation">
          {[...navItems, ...(isAdmin ? [{ label: "Admin", path: "/admin", icon: "admin" }] : [])].map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => isMobile && setDrawerOpen(false)}
              title={isDesktopCollapsed ? item.label : undefined}
              aria-label={isDesktopCollapsed ? item.label : undefined}
              className={({ isActive }) =>
                `nav-item ${isActive ? "nav-item-active" : ""}`
              }
            >
              <span className="nav-icon">{iconMap[item.icon]}</span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <button className="sign-out" type="button" onClick={handleSignOut} title={isDesktopCollapsed ? "Sign out" : undefined} aria-label={isDesktopCollapsed ? "Sign out" : undefined}>
          <SignOutIcon className="icon" />
          <span>Sign out</span>
        </button>
      </aside>
      <main
        className={`content${isEdgeToEdgeRoute ? " content-edge" : ""}${isLookupFindProRoute ? " content-edge-lookup" : ""}${isPlansRoute ? " content-edge-plans" : ""}`}
        id="main-content"
        ref={contentRef}
        onScroll={handleScrollState}
      >
        {isMobile && (
          <header className={`mobile-topbar ${scrolled ? "scrolled" : ""}`}>
            <div className="mobile-topbar-inner">
              <button type="button" className="brand-inline brand-btn" onClick={() => navigate("/dashboard")}>
                <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
              </button>
              <button
                ref={drawerToggleRef}
                className="icon-btn mobile-menu-btn shell-mobile-menu-btn"
                type="button"
                onClick={() => setDrawerOpen((open) => !open)}
                aria-label={drawerOpen ? "Close menu" : "Open menu"}
                aria-expanded={drawerOpen}
                aria-controls="app-shell-navigation"
              >
                <span className="burger-line" aria-hidden="true" />
              </button>
            </div>
          </header>
        )}
        <Outlet />
      </main>
    </div>
  );
}
